import React, { useState, useEffect, useCallback, useRef } from 'react';
import Podium from './Podium';
import axios from 'axios';
import './Leaderboard.css';
import './TribeLeaderboard.css';
import { getDefaultClusterConfig } from '../utils/clusterConfig';
import {
  fmtInt,
  fmtHours,
  getRankClass,
  activateOnKey,
  RankMark,
  ExpandHint,
  SectionHead,
  SearchBox,
  SelectPlate,
  SortableTh,
  ErrorState,
  EmptyState,
  Pagination,
  displayName
} from './LeaderboardParts';

// What the podium shows for each sort. No podium for Deaths: a "champion" of dying reads wrong.
const PODIUM_STAT = {
  'Tribe Score': { label: 'score', value: (t) => fmtInt(t.DamageScore) },
  'Kills': { label: 'kills', value: (t) => fmtInt(t.TotalKills) },
  'Tamed Dino Kills': { label: 'tame kills', value: (t) => fmtInt(t.TotalTameKills) },
  'Time Played': { label: 'hours', value: (t) => fmtHours(t.TotalPlayTime) },
};

const TribeLeaderboard = ({ initialParams = {}, updateURL = () => {} }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState(initialParams.search || '');
  const [filter, setFilter] = useState(initialParams.filter || 'Tribe Score');
  const [cluster, setCluster] = useState(initialParams.cluster || 'SOLO');
  const [currentPage, setCurrentPage] = useState(initialParams.page || 0);
  const [pagination, setPagination] = useState({});
  const [clusterDropdownOpen, setClusterDropdownOpen] = useState(false);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [availableClusters, setAvailableClusters] = useState([{ key: 'SOLO', display: 'Solos' }]);
  const [searchMode, setSearchMode] = useState(!!initialParams.search);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [timeAgo, setTimeAgo] = useState('');
  const [expandedTribe, setExpandedTribe] = useState(null);
  const [tribeMembers, setTribeMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);

  // Refs for dropdown containers
  const clusterDropdownRef = useRef(null);
  const filterDropdownRef = useRef(null);

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only handle if dropdowns are open
      if (!clusterDropdownOpen && !filterDropdownOpen) return;
      
      if (clusterDropdownRef.current && !clusterDropdownRef.current.contains(event.target)) {
        setClusterDropdownOpen(false);
      }
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setFilterDropdownOpen(false);
      }
    };

    // Only add listener when dropdowns are open
    if (clusterDropdownOpen || filterDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [clusterDropdownOpen, filterDropdownOpen]);

  // Sync state to URL
  useEffect(() => {
    updateURL({ tab: 'tribe', cluster, filter, page: currentPage, search: searchMode ? search : '' });
  }, [cluster, filter, currentPage, search, searchMode, updateURL]);

  // Auto-refresh "last updated" display every 30s
  useEffect(() => {
    if (!lastUpdated) return;
    const updateTimeAgo = () => {
      const seconds = Math.floor((Date.now() - new Date(lastUpdated).getTime()) / 1000);
      if (seconds < 60) setTimeAgo('just now');
      else if (seconds < 3600) setTimeAgo(`${Math.floor(seconds / 60)}m ago`);
      else setTimeAgo(`${Math.floor(seconds / 3600)}h ago`);
    };
    updateTimeAgo();
    const interval = setInterval(updateTimeAgo, 30000);
    return () => clearInterval(interval);
  }, [lastUpdated]);

  const filterOptions = [
    'Tribe Score',
    'Kills',
    'Deaths',
    'Tamed Dino Kills',
    'Time Played'
  ];

  // Fetch available clusters on component mount
  useEffect(() => {
    const fetchClusters = async () => {
      try {
        const response = await axios.get('/api/ark/tribe_rankings/clusters');
        if (response.data.clusters && response.data.clusters.length > 0) {
          const clusters = response.data.clusters;
          setAvailableClusters(clusters);
          const currentClusterExists = clusters.find(c => c.key === cluster);
          if (!currentClusterExists) {
            setCluster(clusters[0].key);
          }
        } else {
          console.warn('API returned empty clusters array, using fallback');
          setAvailableClusters(getDefaultClusterConfig());
        }
      } catch (error) {
        console.error('Error fetching available clusters:', error);
        setAvailableClusters(getDefaultClusterConfig());
      }
    };
    fetchClusters();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/ark/tribe_rankings', {
        params: {
          search: '',
          filter: filter,
          cluster,
          page: currentPage
        }
      });
      setData(response.data.ranking_data);
      setPagination(response.data.pagination);
      if (response.data.lastUpdated) setLastUpdated(response.data.lastUpdated);
      setError(false);
    } catch (error) {
      console.error('Error fetching tribe data:', error);
      setData([]);
      setPagination({});
      setError(true);
    }
    setLoading(false);
  }, [filter, cluster, currentPage]);

  const searchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/ark/tribe_rankings', {
        params: {
          search: search,
          filter: filter,
          cluster,
          page: currentPage
        }
      });
      setData(response.data.ranking_data);
      setPagination(response.data.pagination);
      if (response.data.lastUpdated) setLastUpdated(response.data.lastUpdated);
      setError(false);
    } catch (error) {
      console.error('Error fetching tribe data:', error);
      setData([]);
      setPagination({});
      setError(true);
    }
    setLoading(false);
  }, [search, filter, cluster, currentPage]);

  const searchDataWithFilter = useCallback(async (filterToUse) => {
    setLoading(true);
    try {
      const response = await axios.get('/api/ark/tribe_rankings', {
        params: {
          search: search,
          filter: filterToUse,
          cluster,
          page: currentPage
        }
      });
      setData(response.data.ranking_data);
      setPagination(response.data.pagination);
      if (response.data.lastUpdated) setLastUpdated(response.data.lastUpdated);
      setError(false);
    } catch (error) {
      console.error('Error fetching tribe data:', error);
      setData([]);
      setPagination({});
      setError(true);
    }
    setLoading(false);
  }, [search, cluster, currentPage]);

  const searchDataWithCluster = useCallback(async (clusterToUse) => {
    setLoading(true);
    try {
      const response = await axios.get('/api/ark/tribe_rankings', {
        params: {
          search: search,
          filter: filter,
          cluster: clusterToUse,
          page: currentPage
        }
      });
      setData(response.data.ranking_data);
      setPagination(response.data.pagination);
      if (response.data.lastUpdated) setLastUpdated(response.data.lastUpdated);
      setError(false);
    } catch (error) {
      console.error('Error fetching tribe data:', error);
      setData([]);
      setPagination({});
      setError(true);
    }
    setLoading(false);
  }, [search, filter, currentPage]);

  useEffect(() => {
    if (!searchMode) {
      fetchData();
    } else {
      searchData();
    }
  }, [fetchData, searchData, searchMode]);

  const cleanTribeName = (name) => {
    if (!name) return name;
    const fixed = displayName(name);
    return fixed.startsWith('Tribe of ') ? fixed.slice(9) : fixed;
  };

  // Tribe totals come back from MySQL as strings ("4", "0"), so compare as
  // numbers; otherwise a 0-death tribe never gets the perfect-K/D treatment.
  const formatKD = (kd, kills, deaths) => {
    const k = Number(kills) || 0;
    const d = Number(deaths) || 0;
    // Match the server's two-decimal K/D ("22.00") in every case.
    if (d === 0 && k > 0) return <span className="kd-perfect">{k.toFixed(2)}</span>;
    if (Number(kd) === 0 && d === 0) return '0.00';
    return kd;
  };

  const toggleTribeExpand = async (tribeId) => {
    if (expandedTribe === tribeId) {
      setExpandedTribe(null);
      setTribeMembers([]);
      return;
    }
    setExpandedTribe(tribeId);
    setMembersLoading(true);
    try {
      const response = await axios.get('/api/ark/tribe_rankings/members', {
        params: { tribeId, cluster }
      });
      setTribeMembers(response.data.members || []);
    } catch (err) {
      console.error('Error fetching tribe members:', err);
      setTribeMembers([]);
    }
    setMembersLoading(false);
  };

  const debounceRef = useRef(null);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value.trim()) {
      setSearchMode(false);
      return;
    }
    debounceRef.current = setTimeout(() => {
      setCurrentPage(0);
      setSearchMode(true);
    }, 300);
  };

  const handleSearchSubmit = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setCurrentPage(0);
    setSearchMode(true);
    searchData();
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(0);
    setFilterDropdownOpen(false);
    // If there's a search term, use searchData and stay in search mode
    if (search.trim()) {
      // Force a new search with the updated filter by calling searchData directly
      // We need to use the newFilter value directly since setFilter is asynchronous
      searchDataWithFilter(newFilter);
    } else {
      setSearchMode(false);
    }
  };

  const handleClusterChange = (newCluster) => {
    setCluster(newCluster);
    setCurrentPage(0);
    setClusterDropdownOpen(false);
    // If there's a search term, use searchData and stay in search mode
    if (search.trim()) {
      // Force a new search with the updated cluster by calling searchData directly
      // We need to use the newCluster value directly since setCluster is asynchronous
      searchDataWithCluster(newCluster);
    } else {
      setSearchMode(false);
    }
  };

  const clusterDisplay = availableClusters.find(c => c.key === cluster)?.display || cluster;
  // The top three get the podium; the table then starts at #4.
  const showPodium = !loading && !error && currentPage === 0 && !(searchMode && search.trim()) && !!PODIUM_STAT[filter];
  const sortedCls = (option) => (filter === option ? ' is-sorted' : '');
  const emptyMessage = searchMode && search.trim()
    ? `No tribes found matching "${search}"`
    : 'Servers have recently wiped. Please check again later.';
  const emptyKicker = searchMode && search.trim() ? '// No results' : '// No data';

  return (
    <div className="lb lb--tribe tribe-leaderboard">
      <SectionHead kicker="// Tribe rankings" title={clusterDisplay} timeAgo={timeAgo} />

      <div className="controls">
        <SearchBox
          value={search}
          placeholder="Search for Tribes"
          onChange={handleSearchChange}
          onSubmit={handleSearchSubmit}
        />

        <div className="filters">
          <SelectPlate
            groupRef={clusterDropdownRef}
            label="Cluster"
            value={clusterDisplay}
            open={clusterDropdownOpen}
            onToggle={() => setClusterDropdownOpen(!clusterDropdownOpen)}
            options={availableClusters.map(option => ({ key: option.key, label: option.display }))}
            selectedKey={cluster}
            onSelect={handleClusterChange}
          />
          <SelectPlate
            groupRef={filterDropdownRef}
            label="Sort by"
            value={filter}
            open={filterDropdownOpen}
            onToggle={() => setFilterDropdownOpen(!filterDropdownOpen)}
            options={filterOptions.map(option => ({ key: option, label: option }))}
            selectedKey={filter}
            onSelect={handleFilterChange}
          />
        </div>
      </div>

      {showPodium && (
        <Podium
          entries={data
            .map((item, index) => ({ item, rank: Number(item.rank) || index + 1 }))
            .filter(({ rank }) => rank <= 3)
            .map(({ item, rank }) => ({
              rank,
              name: cleanTribeName(item.TribeName),
              sub: `${fmtInt(item.TotalKills)} kills · ${item.KD ?? '0'} K/D`,
              statLabel: PODIUM_STAT[filter].label,
              statValue: PODIUM_STAT[filter].value(item),
              onOpen: () => toggleTribeExpand(item.TribeID),
              openLabel: 'Show tribe members',
            }))}
        />
      )}

      <div className="table-container clip-corner">
        {/* Desktop Table */}
        <table className="leaderboard-table">
          <thead>
            <tr>
              <th scope="col" className="col-rank">Rank</th>
              <th scope="col" className="col-name">Tribe</th>
              <SortableTh className="col-num" label="Tribe Score" active={filter === 'Tribe Score'} onClick={() => handleFilterChange('Tribe Score')} />
              <SortableTh className="col-num" label="Kills" active={filter === 'Kills'} onClick={() => handleFilterChange('Kills')} />
              <SortableTh className="col-num" label="Deaths" active={filter === 'Deaths'} onClick={() => handleFilterChange('Deaths')} />
              <th scope="col" className="col-num">K/D</th>
              <SortableTh className="col-num" label="Tamed Dino Kills" active={filter === 'Tamed Dino Kills'} onClick={() => handleFilterChange('Tamed Dino Kills')} />
              <SortableTh className="col-num" label="Time Played" active={filter === 'Time Played'} onClick={() => handleFilterChange('Time Played')} />
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <tr key={`skel-${i}`} className="skeleton-row">
                  <td className="col-rank"><div className="skeleton-cell skeleton-rank" /></td>
                  <td className="col-name"><div className="skeleton-cell skeleton-name" /></td>
                  <td className="col-num"><div className="skeleton-cell skeleton-num" /></td>
                  <td className="col-num"><div className="skeleton-cell skeleton-num" /></td>
                  <td className="col-num"><div className="skeleton-cell skeleton-num" /></td>
                  <td className="col-num"><div className="skeleton-cell skeleton-num" /></td>
                  <td className="col-num"><div className="skeleton-cell skeleton-num" /></td>
                  <td className="col-num"><div className="skeleton-cell skeleton-num" /></td>
                </tr>
              ))
            ) : error ? (
              <tr className="state-row">
                <td colSpan="8">
                  <ErrorState onRetry={() => fetchData()} />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr className="state-row">
                <td colSpan="8">
                  <EmptyState kicker={emptyKicker}>{emptyMessage}</EmptyState>
                </td>
              </tr>
            ) : (
              data.map((item, index) => {
                const rank = Number(item.rank) || index + 1;
                if (showPodium && rank <= 3 && expandedTribe !== item.TribeID) return null; // on the podium unless opened from it
                const expanded = expandedTribe === item.TribeID;
                const toggle = () => toggleTribeExpand(item.TribeID);
                return (
                  <React.Fragment key={index}>
                    <tr
                      className={`clickable-row ${getRankClass(rank)}${expanded ? ' is-expanded' : ''}`}
                      style={{ '--i': index }}
                      onClick={toggle}
                      onKeyDown={activateOnKey(toggle)}
                      tabIndex={0}
                      aria-expanded={expanded}
                    >
                      <td className="col-rank"><RankMark rank={rank} /></td>
                      <td className="col-name">
                        <div className="name-cell">
                          <span className="name-text tribe-name">{cleanTribeName(item.TribeName)}</span>
                          <ExpandHint open={expanded} />
                        </div>
                      </td>
                      <td className={`col-num${sortedCls('Tribe Score')}`}>{fmtInt(item.DamageScore)}</td>
                      <td className={`col-num${sortedCls('Kills')}`}>{fmtInt(item.TotalKills)}</td>
                      <td className={`col-num${sortedCls('Deaths')}`}>{fmtInt(item.TotalDeaths)}</td>
                      <td className="col-num">{formatKD(item.KD, item.TotalKills, item.TotalDeaths)}</td>
                      <td className={`col-num${sortedCls('Tamed Dino Kills')}`}>{fmtInt(item.TotalTameKills)}</td>
                      <td className={`col-num${sortedCls('Time Played')}`}>{fmtHours(item.TotalPlayTime)}<span className="unit"> hrs</span></td>
                    </tr>
                    {expanded && (
                      <tr className="tribe-members-row">
                        <td colSpan="8">
                          <div className="tribe-members-container">
                            <div className="tribe-members-title hud-label">
                              <span className="kicker-line" />
                              Roster{!membersLoading && tribeMembers.length > 0 ? ` // ${tribeMembers.length} ${tribeMembers.length === 1 ? 'member' : 'members'}` : ''}
                            </div>
                            {membersLoading ? (
                              <div className="tribe-members-loading">Loading members...</div>
                            ) : tribeMembers.length === 0 ? (
                              <div className="tribe-members-loading">No members found</div>
                            ) : (
                              <table className="tribe-members-table">
                                <thead>
                                  <tr>
                                    <th scope="col">Player</th>
                                    <th scope="col" className="col-num">Kills</th>
                                    <th scope="col" className="col-num">Deaths</th>
                                    <th scope="col" className="col-num">K/D</th>
                                    <th scope="col" className="col-num">Dino Kills</th>
                                    <th scope="col" className="col-num">Time</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {tribeMembers.map((m, mi) => (
                                    <tr key={mi}>
                                      <td className="member-name">{displayName(m.PlayerName)}</td>
                                      <td className="col-num">{fmtInt(m.PlayerKills)}</td>
                                      <td className="col-num">{fmtInt(m.DeathByPlayer)}</td>
                                      <td className="col-num">{m.DeathByPlayer > 0 ? (m.PlayerKills / m.DeathByPlayer).toFixed(1) : m.PlayerKills > 0 ? <span className="kd-perfect">{m.PlayerKills}.0</span> : '0.0'}</td>
                                      <td className="col-num">{fmtInt(m.DinoKills)}</td>
                                      <td className="col-num">{fmtHours(m.PlayTime)}<span className="unit"> hrs</span></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>

        {/* Mobile Cards */}
        <div className="mobile-cards">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={`mskel-${i}`} className="mobile-card skeleton-card clip-corner-sm">
                <div className="skeleton-cell skeleton-name" style={{ width: '60%', marginBottom: 12 }} />
                <div className="mobile-stats">
                  {Array.from({ length: 6 }).map((_, j) => (
                    <div key={j} className="mobile-stat"><div className="skeleton-cell skeleton-num" /></div>
                  ))}
                </div>
              </div>
            ))
          ) : error ? (
            <ErrorState onRetry={() => fetchData()} />
          ) : data.length === 0 ? (
            <EmptyState kicker={emptyKicker}>{emptyMessage}</EmptyState>
          ) : (
            data.map((item, index) => {
              const rank = Number(item.rank) || index + 1;
                if (showPodium && rank <= 3 && expandedTribe !== item.TribeID) return null; // on the podium unless opened from it
              const expanded = expandedTribe === item.TribeID;
              const toggle = () => toggleTribeExpand(item.TribeID);
              return (
                <div
                  key={index}
                  className={`mobile-card clip-corner-sm ${getRankClass(rank)}${expanded ? ' is-expanded' : ''}`}
                  style={{ '--i': index }}
                >
                  <div
                    className="mobile-card-header clickable-row"
                    role="button"
                    tabIndex={0}
                    aria-expanded={expanded}
                    onClick={toggle}
                    onKeyDown={activateOnKey(toggle)}
                  >
                    <div className="mobile-rank"><RankMark rank={rank} /></div>
                    <div className="mobile-card-info">
                      <div className="mobile-card-name">{cleanTribeName(item.TribeName)}</div>
                    </div>
                    <ExpandHint open={expanded} />
                  </div>
                  <div className="mobile-stats">
                    <div className={`mobile-stat${sortedCls('Tribe Score')}`}>
                      <div className="mobile-stat-label">Tribe Score</div>
                      <div className="mobile-stat-value">{fmtInt(item.DamageScore)}</div>
                    </div>
                    <div className={`mobile-stat${sortedCls('Kills')}`}>
                      <div className="mobile-stat-label">Kills</div>
                      <div className="mobile-stat-value">{fmtInt(item.TotalKills)}</div>
                    </div>
                    <div className={`mobile-stat${sortedCls('Deaths')}`}>
                      <div className="mobile-stat-label">Deaths</div>
                      <div className="mobile-stat-value">{fmtInt(item.TotalDeaths)}</div>
                    </div>
                    <div className="mobile-stat">
                      <div className="mobile-stat-label">K/D</div>
                      <div className="mobile-stat-value">{formatKD(item.KD, item.TotalKills, item.TotalDeaths)}</div>
                    </div>
                    <div className={`mobile-stat${sortedCls('Tamed Dino Kills')}`}>
                      <div className="mobile-stat-label">Tamed Dino Kills</div>
                      <div className="mobile-stat-value">{fmtInt(item.TotalTameKills)}</div>
                    </div>
                    <div className={`mobile-stat${sortedCls('Time Played')}`}>
                      <div className="mobile-stat-label">Time Played</div>
                      <div className="mobile-stat-value">{fmtHours(item.TotalPlayTime)}<span className="unit"> hrs</span></div>
                    </div>
                  </div>
                  {expanded && (
                    <div className="mobile-members">
                      <div className="tribe-members-title hud-label">
                        <span className="kicker-line" />
                        Roster{!membersLoading && tribeMembers.length > 0 ? ` // ${tribeMembers.length}` : ''}
                      </div>
                      {membersLoading ? (
                        <div className="tribe-members-loading">Loading...</div>
                      ) : tribeMembers.length === 0 ? (
                        <div className="tribe-members-loading">No members found</div>
                      ) : tribeMembers.map((m, mi) => (
                        <div key={mi} className="mobile-member">
                          <span className="mobile-member-name">{displayName(m.PlayerName)}</span>
                          <span className="mobile-member-stat">{fmtInt(m.PlayerKills)} kills</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={pagination.total_pages}
        onPageChange={setCurrentPage}
      />
    </div>
  );
};

export default TribeLeaderboard;
